import { createEffect, createSignal, Show } from 'solid-js';
import { isServer } from 'solid-js/web';
import { ImageWordpress } from '../../types/shared';
import { NewAtosLogoRelative } from '../icons-library/new-atos-logo-relative';
import { getYoutubeThumbnailUrl, getYoutubeVideoId, YOUTUBE_THUMBNAIL_SIZES } from './helpers';
import { PlayAndCircle } from '../../ui-components/play-and-circle/play-and-circle';

type VideoImageBeforeCookieProps = {
    videoUrl: string;
    image?: ImageWordpress;
    bgImage?: string;
    /* Responsive candidates for `bgImage`; ignored for the YouTube-thumbnail fallback. */
    srcset?: string;
    sizes?: string;
    /* Off when something else covers the media (eg. the gated-content lock). */
    hidePlayButton?: boolean;
};

// YouTube answers a missing thumbnail size with a 404 whose body is a 120×90 grey placeholder,
// which browsers render without firing `error`.
const YOUTUBE_PLACEHOLDER_WIDTH = 120;

export const VideoImageBeforeCookiesAccepted = (props: VideoImageBeforeCookieProps) => {
    const [ imgSrc, setImgSrc ] = createSignal<string | undefined>();
    let youtubeFallbacks: string[] = [];

    createEffect(() => {
        youtubeFallbacks = [];

        if (props.bgImage) {
            setImgSrc(props.bgImage);
            return;
        }

        // Covers youtube.com and youtu.be share links alike.
        const youtubeId = props.videoUrl?.includes('youtu') ? getYoutubeVideoId(props.videoUrl) : undefined;
        if (youtubeId) {
            const [ best, ...fallbacks ] = YOUTUBE_THUMBNAIL_SIZES.map((size) => getYoutubeThumbnailUrl(youtubeId, size));
            youtubeFallbacks = fallbacks;
            setImgSrc(best);
            return;
        }

        setImgSrc(undefined);

        // Vimeo has no static thumbnail URL; its oEmbed endpoint is public and CORS-open
        // (`vimeo.com` is allow-listed in the CSP connect-src). Client-only, so SSR shows the
        // logo fallback and the poster fades in on hydration.
        if (props.videoUrl?.includes('vimeo') && !isServer) {
            const requestedFor = props.videoUrl;
            fetch(`https://vimeo.com/api/oembed.json?url=${encodeURIComponent(requestedFor)}&width=960`)
                .then((response) => (response.ok ? response.json() : undefined))
                .then((data) => {
                    if (data?.thumbnail_url && props.videoUrl === requestedFor) {
                        setImgSrc(data.thumbnail_url);
                    }
                })
                .catch(() => { /* keep the logo fallback */ });
        }
    });

    // Guarded on the failing src so a late event for an already-replaced src can't skip a size.
    const tryNextYoutubeThumbnail = (failedSrc: string) => {
        if (failedSrc !== imgSrc() || !youtubeFallbacks.length) {
            return;
        }

        setImgSrc(youtubeFallbacks.shift());
    };

    const checkLoadedImage = (img: HTMLImageElement) => {
        if (img.naturalWidth === 0 || img.naturalWidth === YOUTUBE_PLACEHOLDER_WIDTH) {
            tryNextYoutubeThumbnail(img.src);
        }
    };

    return (
        <>
            <Show when={!props.hidePlayButton}>
                <div class="absolute z-[2] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 cursor-pointer">
                    <PlayAndCircle/>
                </div>
            </Show>

            <Show
                fallback={(
                    <div class="absolute flex justify-center items-center w-full h-auto top-1/2 -translate-y-1/2">
                        <NewAtosLogoRelative />
                    </div>
                )}
                when={imgSrc()}
            >
                <img
                    class="absolute inset-0 size-full object-cover object-center"
                    src={imgSrc()}
                    srcset={props.bgImage ? props.srcset : undefined}
                    sizes={props.bgImage ? props.sizes : undefined}
                    loading="lazy"
                    onLoad={(e) => checkLoadedImage(e.currentTarget)}
                    onError={(e) => tryNextYoutubeThumbnail(e.currentTarget.src)}
                    // The SSR'd image may finish loading before hydration attaches the handlers above.
                    ref={(img) => queueMicrotask(() => img.complete && checkLoadedImage(img))}
                    alt={props?.image?.alt || ''}
                    {...props?.image?.alt
                        ? {}
                        : { role: 'presentation' }
                    }
                />
            </Show>
        </>
    );
};
